<template>
  <!-- items-stretch: кнопка галереи берёт высоту у основной, какой бы та ни
       была (h-11, h-12, py-3 у пунктирной рамки) — размеры не приходится
       передавать пропом и держать в синхроне с семью местами вызова.
       Ширину задаём отступами, а не aspect-square: во флекс-строке ширина
       элемента считается по содержимому раньше, чем высота растянется, и
       квадрат из этого не получается — иконка оказывается впритык к краям.
       22 пикселя иконки плюс 12 слева и справа дают 46 при высоте 44–48.

       px-3 на основной кнопке: до появления кнопки галереи все семь были
       w-full во всю ширину диалога и в полях не нуждались. Кнопка галереи
       забрала 46 пикселей плюс зазор, и подписи упёрлись в края — на 360px
       у «Сфотографировать штрихкод» оставалось по 3 пикселя. Не px-4: при
       16 пикселях поля «Сфотографировать этикетку» перестаёт помещаться в
       строку и переносится внутрь фиксированной h-12. Поля живут здесь, а не
       в buttonClass у вызовов: свой px в вызове не переопределит этот, а
       сложится с ним — порядок классов в атрибуте на приоритет не влияет. -->
  <div class="flex items-stretch" :class="wrapClass">
    <button
      type="button"
      :disabled="disabled"
      :class="buttonClass"
      class="flex-1 min-w-0 px-3"
      @click="openCamera"
    >
      <slot />
    </button>

    <button
      type="button"
      :disabled="disabled"
      aria-label="Выбрать из галереи"
      :class="galleryClass"
      class="shrink-0 px-3 flex items-center justify-center"
      @click="openGallery"
    >
      <svg width="22" height="22" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.5 13.5H12.5C13.0523 13.5 13.5 13.0523 13.5 12.5V1.5C13.5 0.947715 13.0523 0.5 12.5 0.5H1.5C0.947715 0.5 0.5 0.947715 0.5 1.5V12.5C0.5 13.0523 0.947715 13.5 1.5 13.5Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M1.75 13.5L9.05 6.61996C9.13984 6.54513 9.25307 6.50415 9.37 6.50415C9.48692 6.50415 9.60015 6.54513 9.68999 6.61996L13.5 9.34996" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M4.5 6C5.32843 6 6 5.32843 6 4.5C6 3.67157 5.32843 3 4.5 3C3.67157 3 3 3.67157 3 4.5C3 5.32843 3.67157 6 4.5 6Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>

    <input ref="cameraInput" type="file" accept="image/*" capture="environment" class="hidden" @change="onFile" />
    <input ref="galleryInput" type="file" accept="image/*" class="hidden" @change="onFile" />
  </div>
</template>

<script setup>
/**
 * Кнопка съёмки с соседней кнопкой «выбрать из галереи».
 *
 * Зачем компонент, а не просто снятый с инпута capture="environment": без
 * второй кнопки пользователь не узнаёт, что готовое фото вообще можно
 * подложить, а системный выбор на Android ведёт себя по-разному от прошивки
 * к прошивке. Плюс здесь же живёт разрешение камеры для Android-PWA —
 * раньше это знание было только в AddFoodDialog, и остальные точки съёмки
 * молча не открывали камеру.
 *
 * Наружу отдаётся сам File, а не событие: чистить input.value после выбора
 * (иначе повторный выбор того же файла не сработает) — забота компонента.
 */
import { ref } from 'vue'

const props = defineProps({
  // Классы основной кнопки: у семи мест вызова своя вёрстка, и навязывать
  // им общий вид значило бы переделывать экраны заодно с этой правкой.
  buttonClass: { type: String, default: '' },
  galleryClass: {
    type: String,
    default: 'rounded-xl border border-[#E0D9CF] bg-white text-ink active:bg-[#F5EFEA] disabled:opacity-50 transition-colors',
  },
  wrapClass: { type: String, default: 'gap-2' },
  disabled: { type: Boolean, default: false },
  // Текст в подсказке, если Android-PWA не дал доступ к камере.
  cameraDeniedText: {
    type: String,
    default: 'Для съёмки нужен доступ к камере. Разрешите доступ в настройках браузера.',
  },
})

const emit = defineEmits(['file'])

const cameraInput = ref(null)
const galleryInput = ref(null)

async function openCamera() {
  // Android PWA: без предварительного запроса разрешения input с capture
  // молча не срабатывает — пользователь жмёт кнопку и ничего не происходит.
  const isAndroid = /android/i.test(navigator.userAgent)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
  if (isAndroid && isStandalone && navigator.mediaDevices?.getUserMedia) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true })
      stream.getTracks().forEach(t => t.stop())
    } catch (e) {
      alert(props.cameraDeniedText)
      return
    }
  }
  cameraInput.value?.click()
}

function openGallery() {
  galleryInput.value?.click()
}

function onFile(e) {
  const file = e.target.files?.[0]
  e.target.value = ''
  if (file) emit('file', file, e.target === galleryInput.value ? 'gallery' : 'camera')
}
</script>
